import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);
    const [pendingLogout, setPendingLogout] = useState(false);
    const [reservationCount, setReservationCount] = useState(0);

    const performLogout = useCallback(async () => {
        // Call backend to release reservations
        if (token) {
            try {
                await axios.post(`${API_URL}/auth/logout`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } catch (error) {
                console.error('Logout error:', error);
            }
        }
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setPendingLogout(false);
        setReservationCount(0);
    }, [token]);

    const checkReservations = useCallback(async () => {
        if (!token) return { has_reservations: false, count: 0 };
        
        try {
            const response = await axios.get(`${API_URL}/auth/check-reservations`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Check reservations error:', error);
            return { has_reservations: false, count: 0 };
        }
    }, [token]);

    const logout = useCallback(async () => {
        // Check if user has active reservations
        const { has_reservations, count } = await checkReservations();
        
        if (has_reservations && !user?.is_admin) {
            // Show confirmation dialog
            setReservationCount(count);
            setPendingLogout(true);
        } else {
            // No reservations, logout directly
            await performLogout();
        }
    }, [checkReservations, performLogout, user]);

    const confirmLogout = useCallback(async () => {
        await performLogout();
    }, [performLogout]);

    const cancelLogout = useCallback(() => {
        setPendingLogout(false);
        setReservationCount(0);
    }, []);

    useEffect(() => {
        const fetchUser = async () => {
            if (token) {
                try {
                    const response = await axios.get(`${API_URL}/auth/me`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setUser(response.data);
                } catch (error) {
                    console.error('Auth error:', error);
                    performLogout();
                }
            }
            setLoading(false);
        };
        fetchUser();
    }, [token, performLogout]);

    const login = async (email, password) => {
        const response = await axios.post(`${API_URL}/auth/login`, { email, password });
        const { token: newToken, user: userData } = response.data;
        localStorage.setItem('token', newToken);
        setToken(newToken);
        setUser(userData);
        return userData;
    };

    const register = async (userData) => {
        const response = await axios.post(`${API_URL}/auth/register`, userData);
        return response.data;
    };

    // Heartbeat to track online status (every 60 seconds for non-admin users)
    useEffect(() => {
        if (token && user && !user.is_admin) {
            const sendHeartbeat = async () => {
                try {
                    await axios.post(`${API_URL}/activity/heartbeat`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                } catch (error) {
                    // Silently fail - don't disrupt user experience
                    console.debug('Heartbeat failed:', error);
                }
            };
            
            // Send initial heartbeat
            sendHeartbeat();
            
            // Send heartbeat every 60 seconds
            const interval = setInterval(sendHeartbeat, 60000);
            
            return () => clearInterval(interval);
        }
    }, [token, user]);

    const value = {
        user,
        token,
        loading,
        login,
        logout,
        confirmLogout,
        cancelLogout,
        register,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin' || user?.is_admin === true,
        isApproved: user?.status === 'approved' || user?.is_admin === true,
        pendingLogout,
        reservationCount,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
